/**
 * Tenant Navigation Provider
 *
 * Provides tenant-specific navigation customization:
 * - Hide/show specific navigation items
 * - Hide/show entire navigation groups
 * - Custom labels for navigation items (optional)
 * - Navigation filtering based on tenant features
 *
 * Part of: AI Platform Phase 3.1 - UI/UX Enhancement & Tenant Customization
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useTenantOptional } from '../contexts/TenantContext';

// =============================================================================
// Types
// =============================================================================

/**
 * Navigation item configuration
 */
export interface NavItemConfig {
  /** Navigation item ID (e.g., 'users', 'logs', 'dashboard') */
  id: string;
  /** Custom label override (optional) */
  label?: string;
  /** Whether the item is hidden */
  hidden?: boolean;
  /** Required feature flag to show this item */
  requiredFeature?: string;
}

/**
 * Navigation group configuration
 */
export interface NavGroupConfig {
  /** Navigation group ID (e.g., 'access', 'operations') */
  id: string;
  /** Custom label override (optional) */
  label?: string;
  /** Whether the entire group is hidden */
  hidden?: boolean;
  /** Item-level overrides within this group */
  items?: NavItemConfig[];
}

/**
 * Tenant navigation configuration stored in tenant.navigation_overrides
 */
export interface TenantNavigationConfig {
  /** List of hidden navigation item IDs (simple hide list) */
  hiddenItems?: string[];
  /** List of hidden navigation group IDs */
  hiddenGroups?: string[];
  /** Detailed group configurations (for advanced customization) */
  groups?: NavGroupConfig[];
  /** Detailed item configurations (for advanced customization) */
  items?: NavItemConfig[];
  /** Custom home/landing page (defaults to 'dashboard') */
  defaultTab?: string;
}

/**
 * Generic navigation item interface
 */
export interface NavigationItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

/**
 * Generic navigation group interface
 */
export interface NavigationGroup {
  id: string;
  label: string;
  icon?: React.ReactNode;
  items: NavigationItem[];
}

/**
 * Navigation context value
 */
export interface TenantNavigationContextValue {
  /** Check if a navigation item should be visible */
  isItemVisible: (itemId: string, groupId?: string) => boolean;
  /** Check if a navigation group should be visible */
  isGroupVisible: (groupId: string) => boolean;
  /** Get custom label for an item (or undefined if using default) */
  getItemLabel: (itemId: string) => string | undefined;
  /** Get custom label for a group (or undefined if using default) */
  getGroupLabel: (groupId: string) => string | undefined;
  /** Filter an array of navigation items */
  filterItems: <T extends NavigationItem>(items: T[]) => T[];
  /** Filter an array of navigation groups (including their items) */
  filterGroups: <T extends NavigationGroup>(groups: T[]) => T[];
  /** Get the default tab for this tenant */
  getDefaultTab: () => string;
  /** Raw navigation config for advanced use cases */
  config: TenantNavigationConfig;
}

// =============================================================================
// Context
// =============================================================================

const TenantNavigationContext = createContext<TenantNavigationContextValue | null>(null);

// =============================================================================
// Default Configuration
// =============================================================================

const DEFAULT_CONFIG: TenantNavigationConfig = {
  hiddenItems: [],
  hiddenGroups: [],
  groups: [],
  items: [],
  defaultTab: 'dashboard',
};

// =============================================================================
// Provider Component
// =============================================================================

interface TenantNavigationProviderProps {
  children: ReactNode;
}

export const TenantNavigationProvider: React.FC<TenantNavigationProviderProps> = ({
  children,
}) => {
  const tenantContext = useTenantOptional();
  const tenant = tenantContext?.tenant;

  // Parse navigation config from tenant
  const config: TenantNavigationConfig = useMemo(() => {
    if (!tenant) return DEFAULT_CONFIG;

    // Navigation config is stored in a generic field, need to type it
    const navOverrides = (tenant as unknown as { navigation_overrides?: TenantNavigationConfig }).navigation_overrides;

    if (!navOverrides) return DEFAULT_CONFIG;

    return {
      hiddenItems: navOverrides.hiddenItems || [],
      hiddenGroups: navOverrides.hiddenGroups || [],
      groups: navOverrides.groups || [],
      items: navOverrides.items || [],
      defaultTab: navOverrides.defaultTab || 'dashboard',
    };
  }, [tenant]);

  // Build lookup maps for efficient checking
  const { hiddenItemsSet, hiddenGroupsSet, itemConfigMap, groupConfigMap } = useMemo(() => {
    const hiddenItemsSet = new Set(config.hiddenItems || []);
    const hiddenGroupsSet = new Set(config.hiddenGroups || []);

    // Build item config map
    const itemConfigMap = new Map<string, NavItemConfig>();
    for (const item of config.items || []) {
      itemConfigMap.set(item.id, item);
    }
    // Also add items from group configs
    for (const group of config.groups || []) {
      for (const item of group.items || []) {
        itemConfigMap.set(`${group.id}:${item.id}`, item);
      }
    }

    // Build group config map
    const groupConfigMap = new Map<string, NavGroupConfig>();
    for (const group of config.groups || []) {
      groupConfigMap.set(group.id, group);
    }

    return { hiddenItemsSet, hiddenGroupsSet, itemConfigMap, groupConfigMap };
  }, [config]);

  // Check if tenant has a feature enabled
  const hasFeature = useMemo(() => {
    const features = tenant?.features || [];
    const featureSet = new Set(features);
    return (feature: string) => featureSet.has(feature);
  }, [tenant?.features]);

  /**
   * Check if a navigation item should be visible
   */
  const isItemVisible = (itemId: string, groupId?: string): boolean => {
    // Check simple hidden list
    if (hiddenItemsSet.has(itemId)) return false;

    // Check item-level config
    const itemConfig = itemConfigMap.get(itemId) ||
                       (groupId ? itemConfigMap.get(`${groupId}:${itemId}`) : undefined);

    if (itemConfig) {
      // Explicitly hidden
      if (itemConfig.hidden) return false;
      // Requires a feature that tenant doesn't have
      if (itemConfig.requiredFeature && !hasFeature(itemConfig.requiredFeature)) {
        return false;
      }
    }

    return true;
  };

  /**
   * Check if a navigation group should be visible
   */
  const isGroupVisible = (groupId: string): boolean => {
    // Check simple hidden list
    if (hiddenGroupsSet.has(groupId)) return false;

    // Check group-level config
    const groupConfig = groupConfigMap.get(groupId);
    if (groupConfig?.hidden) return false;

    return true;
  };

  /**
   * Get custom label for an item
   */
  const getItemLabel = (itemId: string): string | undefined => {
    const itemConfig = itemConfigMap.get(itemId);
    return itemConfig?.label;
  };

  /**
   * Get custom label for a group
   */
  const getGroupLabel = (groupId: string): string | undefined => {
    const groupConfig = groupConfigMap.get(groupId);
    return groupConfig?.label;
  };

  /**
   * Filter an array of navigation items
   */
  const filterItems = <T extends NavigationItem>(items: T[]): T[] => {
    return items.filter(item => isItemVisible(item.id));
  };

  /**
   * Filter an array of navigation groups (including their items)
   */
  const filterGroups = <T extends NavigationGroup>(groups: T[]): T[] => {
    return groups
      .filter(group => isGroupVisible(group.id))
      .map(group => ({
        ...group,
        items: group.items.filter(item => isItemVisible(item.id, group.id)),
      }))
      .filter(group => group.items.length > 0); // Remove empty groups
  };

  /**
   * Get the default tab for this tenant
   */
  const getDefaultTab = (): string => {
    return config.defaultTab || 'dashboard';
  };

  const value: TenantNavigationContextValue = useMemo(() => ({
    isItemVisible,
    isGroupVisible,
    getItemLabel,
    getGroupLabel,
    filterItems,
    filterGroups,
    getDefaultTab,
    config,
  }), [config, hiddenItemsSet, hiddenGroupsSet, itemConfigMap, groupConfigMap, hasFeature]);

  return (
    <TenantNavigationContext.Provider value={value}>
      {children}
    </TenantNavigationContext.Provider>
  );
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to access tenant navigation configuration
 * Throws if used outside TenantNavigationProvider
 */
export function useTenantNavigation(): TenantNavigationContextValue {
  const context = useContext(TenantNavigationContext);
  if (!context) {
    throw new Error('useTenantNavigation must be used within a TenantNavigationProvider');
  }
  return context;
}

/**
 * Hook to access tenant navigation configuration (optional version)
 * Returns default behavior if used outside TenantNavigationProvider
 */
export function useTenantNavigationOptional(): TenantNavigationContextValue {
  const context = useContext(TenantNavigationContext);

  // Return default implementation if no context
  if (!context) {
    return {
      isItemVisible: () => true,
      isGroupVisible: () => true,
      getItemLabel: () => undefined,
      getGroupLabel: () => undefined,
      filterItems: (items) => items,
      filterGroups: (groups) => groups,
      getDefaultTab: () => 'dashboard',
      config: DEFAULT_CONFIG,
    };
  }

  return context;
}

export default TenantNavigationProvider;
